'use client'

import { useEffect, useRef, useState } from 'react'

/**
 * Hook to calculate vertical offset for dialogs on iOS/Android
 * so they stay visible above the keyboard.
 *
 * Returns { offset }.
 */
export const useVisualViewportOffset = () => {
	const [offset, setOffset] = useState(0)

	useEffect(() => {
		const visualViewport = window.visualViewport
		if (!visualViewport) return

		const handleResize = () => {
			const visibleHeight = visualViewport.height
			const topOffset = (visibleHeight - window.innerHeight) / 2
			setOffset(topOffset)
		}

		visualViewport.addEventListener('resize', handleResize)
		visualViewport.addEventListener('scroll', handleResize)
		handleResize()

		return () => {
			visualViewport.removeEventListener('resize', handleResize)
			visualViewport.removeEventListener('scroll', handleResize)
		}
	}, [])

	return { offset }
}

const KEYBOARD_HEIGHT_THRESHOLD_PX = 150

export const useIsKeyboardOpen = () => {
	const [isKeyboardOpen, setIsKeyboardOpen] = useState(false)
	const maxViewportHeightRef = useRef(0)

	useEffect(() => {
		const visualViewport = window.visualViewport
		if (!visualViewport) return

		const handleResize = () => {
			const currentHeight = visualViewport.height

			if (currentHeight > maxViewportHeightRef.current) {
				maxViewportHeightRef.current = currentHeight
			}

			const keyboardHeight = maxViewportHeightRef.current - currentHeight
			setIsKeyboardOpen(keyboardHeight > KEYBOARD_HEIGHT_THRESHOLD_PX)
		}

		visualViewport.addEventListener('resize', handleResize)

		handleResize()

		return () => {
			visualViewport.removeEventListener('resize', handleResize)
		}
	}, [])

	return isKeyboardOpen
}

export const dialogVisualViewportTransformCls = [
	'transition-transform duration-300 ease-in-out',
	'[transform:translate(-50%,-50%)_translateY(var(--vv-offset))]',
]
